import {type AccessibilityActionEvent} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ArrowTopRight_Stroke2_Corner0_Rounded as ArrowTopRightIcon} from '#/components/icons/Arrow'
import {Link} from '#/components/Link'
import {useSupportPalette} from '../palette'
import {
  PILL_LEFT,
  PILL_RIGHT_ARROW,
  PILL_RIGHT_GRIP,
  PILL_RIGHT_TEXT,
  PILL_VERTICAL,
} from '../pillSize'
import {getLinkHost, getSupportProvider} from '../providers'
import {type ProfileLink} from '../types'
import {DragHandle} from './DragHandle'
import {ButtonFavicon, linkFaviconUrl} from './LinkFavicon'
import {linkIcon} from './ProviderLogo'

export type PillA11yActions = {
  accessibilityActions?: {name: string; label: string}[]
  onAccessibilityAction?: (event: AccessibilityActionEvent) => void
}

/** The owner's title, or the bare domain. */
export function linkLabel(link: ProfileLink): string {
  return link.title || getLinkHost(link.url)
}

/**
 * The pill for a profile link: green with the provider's logo for a
 * recognized support platform, gray with a globe for any other site. The
 * caller decides what a tap does, unless `opensLink` is set.
 */
export function ProfileLinkPill({
  link,
  onPress,
  onLongPress,
  label,
  hint,
  arrow = false,
  handle = false,
  testID = 'profileLinkPill',
  a11yActions,
  did,
  opensLink = false,
}: {
  link: ProfileLink
  /** The profile owner, whose repo holds the link's stored icon. Needed for links without an `iconUrl`. */
  did?: string
  onPress?: () => void
  onLongPress?: () => void
  /** Accessibility label. Defaults to "Open <title>". */
  label?: string
  /** Accessibility hint, e.g. where the link goes. */
  hint?: string
  /** Trailing outbound arrow, for pills that open the link. */
  arrow?: boolean
  /** Show the editor's drag grip in place of the arrow. */
  handle?: boolean
  testID?: string
  a11yActions?: PillA11yActions
  /**
   * Render as a link to the URL, opened through the redirect service so
   * Safelink applies. On web it's a real anchor, so the browser shows where
   * it goes on hover. `onPress` still runs first.
   */
  opensLink?: boolean
}) {
  const {t: l} = useLingui()
  const t = useTheme()
  const palette = useSupportPalette()
  const provider = getSupportProvider(link.url)
  const color = provider ? palette.fg : t.atoms.text.color
  const faviconUrl = provider ? undefined : linkFaviconUrl(link, did)

  const trailing = handle ? (
    <DragHandle onGreen={!!provider} />
  ) : arrow ? (
    <ArrowTopRightIcon width={12} style={{color, marginLeft: -4}} />
  ) : null

  const props = {
    testID,
    label: label ?? l`Open ${linkLabel(link)}`,
    size: 'small',
    color: provider ? 'primary' : 'secondary',
    onLongPress,
    accessibilityHint: hint,
    ...a11yActions,
    style: [
      {
        paddingVertical: PILL_VERTICAL,
        paddingLeft: PILL_LEFT,
        paddingRight: handle
          ? PILL_RIGHT_GRIP
          : arrow
            ? PILL_RIGHT_ARROW
            : PILL_RIGHT_TEXT,
      },
      provider && {backgroundColor: palette.bg},
    ],
    hoverStyle: provider ? {backgroundColor: palette.bgHover} : undefined,
  } as const
  const content = (
    <>
      {faviconUrl ? (
        <ButtonFavicon key={faviconUrl} uri={faviconUrl} />
      ) : (
        <ButtonIcon icon={linkIcon(provider)} />
      )}
      <ButtonText numberOfLines={1} emoji style={{color}}>
        {link.title ? (
          link.title
        ) : provider ? (
          <Trans context="Creator support button, e.g. Support on Ko-fi">
            Support on {provider.name}
          </Trans>
        ) : (
          getLinkHost(link.url)
        )}
      </ButtonText>
      {trailing}
    </>
  )

  if (opensLink) {
    return (
      <Link {...props} to={link.url} onPress={onPress} shouldProxy>
        {content}
      </Link>
    )
  }
  return (
    <Button {...props} onPress={onPress}>
      {content}
    </Button>
  )
}
